import { useState } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import axios from 'axios';
import _ from 'lodash';
import Box from '@mui/material/Box';
import { v4 as uuidv4 } from 'uuid';
import dayjs from 'dayjs';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Modal from '@mui/material/Modal';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableContainer from '@mui/material/TableContainer';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import { red } from '@mui/material/colors';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus, faTableList, faShip, faTruck, faPlane, faEnvelope } from '@fortawesome/free-solid-svg-icons';
import { faPenToSquare, faTrashCan, faClone } from '@fortawesome/free-regular-svg-icons';
import { faWpforms } from '@fortawesome/free-brands-svg-icons';
import FormsLink from '../../organization/FormsLink.js';
import Header from '../../Header.js';

const TRANSPORT_MODES = ['air', 'sea', 'land'];

export default function OpportunityTemplates(props) {
    const { type } = props;
    const { org, onOrgUpdated, accesstoken } = useOutletContext();
    const navigate = useNavigate();
    const [ filtertransportationModes, setFilterTransportationMode ] = useState(TRANSPORT_MODES);
    const [ openDelete, setOpenDelete ] = useState(null);
    const [ deleting, setDeleting ] = useState(null);
    const [ editLinksOpen, setEditLinksOpen ] = useState(false);
    const [ filterFormTypes, setFilterFormTypes ] = useState(['opportunity', 'opportunity-email-template']);

    function onEditLinks() {
        setEditLinksOpen(true);
    }

    function onEdit(id) {
        if (org.forms[id].type === 'opportunity-email-template') {
            navigate(`./emails/${id}`);
        } else {
            navigate(`./${id}`);
        }
    }

    function onDelete(id) {
        setOpenDelete({ id, title: org.forms[id].title });
    }

    async function onDeleteConfirm() {
        setDeleting('deleting');
        const forms = _.cloneDeep(org.forms);
        forms[openDelete.id].deleted = true;
        try {
            await axios.post(`/api/organizations/${org.id}/forms/patch/${openDelete.id}?accesstoken=${accesstoken}`, { deleted: true });
            onOrgUpdated({ ...org, forms: forms });
        } catch(e) {
            setDeleting('error');
            setDeleting(null);
        }
        setDeleting(null);
        setOpenDelete(null);
    }

    function onClone(id) {
        if (org.forms[id].type === 'opportunity-email-template') {
            navigate(`./emails/${uuidv4()}?clone=${id}`);
        } else {
            navigate(`./${uuidv4()}?clone=${id}`);
        }
    }

    return (
        <Box sx={{ paddingBottom: '36px', background: 'white' }}>
            <Header
                icon={faWpforms}
                title={type === 'rate' ? 'Supplier & Rate Forms' : 'Opportunity Templates'}
                buttons={_.compact([{
                    title: 'New Form',
                    icon: faPlus,
                    color: 'monada-button',
                    action: () => navigate(`./${uuidv4()}`),
                }, type === 'opportunity' && {
                    title: 'New Email Template',
                    icon: faEnvelope,
                    color: 'monada-button',
                    action: () => navigate(`./emails/${uuidv4()}`),
                }])}
            />
            <Box style={{ display: 'flex', alignItems: 'center', padding: '0px 24px' }}>
                <Typography variant='caption' sx={{ paddingRight: '12px' }}>Forms:</Typography>
                <FormGroup row>
                    {['opportunity', 'opportunity-email-template'].map(formType => (
                        <FormControlLabel key={formType}
                            control={
                                <Checkbox
                                    size={'small'}
                                    checked={filterFormTypes.includes(formType)}
                                    onChange={(e, t) => { 
                                        setFilterFormTypes(t ? [...filterFormTypes, formType] : _.without(filterFormTypes, formType));
                                    }}
                                />}
                            label={<Typography variant='caption'>{_.startCase(formType)}</Typography>}
                        />
                    ))}
                </FormGroup>
            </Box>
            <Box style={{ display: 'flex', alignItems: 'center', padding: '0px 24px' }}>
                <Typography variant='caption' sx={{ paddingRight: '12px' }}>Transport Modes:</Typography>
                <FormGroup row>
                    {TRANSPORT_MODES.map(transportMode => (
                        <FormControlLabel key={transportMode}
                            control={
                                <Checkbox
                                    size={'small'}
                                    checked={filtertransportationModes.includes(transportMode)}
                                    onChange={(e, t) => { 
                                        setFilterTransportationMode(t ? [...filtertransportationModes, transportMode] : _.without(filtertransportationModes, transportMode));
                                    }}
                                />}
                            label={<Typography variant='caption'>{_.startCase(transportMode)} Transport</Typography>}
                        />
                    ))}
                </FormGroup>
            </Box>
            {type === 'opportunity' && (
                <Box sx={{ padding: '0px 24px' }} >
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ width: '20%' }}></TableCell>
                                    <TableCell sx={{ width: '10%' }}></TableCell>
                                    <TableCell>Created By</TableCell>
                                    <TableCell>Created On</TableCell>
                                    <TableCell></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {_(org.forms).pickBy(f => (filterFormTypes.includes(f.type) && (!f.transportationMethods || f.transportationMethods.length === 0 || _.intersection(filtertransportationModes, f.transportationMethods).length > 0) && !f.deleted)).toPairs().sortBy(f => f[1].title).map(([id, form]) => (
                                    <TableRow key={id}>
                                        <TableCell>
                                            {form.type === 'opportunity-email-template' ? (
                                                <FontAwesomeIcon icon={faEnvelope} style={{ color: '#605de4' }} />
                                            ) : (
                                                <FontAwesomeIcon icon={faTableList} style={{ color: '#605de4' }} />
                                            )}
                                            &nbsp;
                                            &nbsp;
                                            {form.title}
                                            {form.deleted ? ' - deleted' : ''}
                                            {form.type === 'price-quote' ? ' (old style)' : ''}
                                        </TableCell>
                                        <TableCell sx={{ textAlign: 'center' }}>
                                            {
                                                (form.transportationMethods || (form.transportationMethod ? [form.transportationMethod] : TRANSPORT_MODES)).map(t => (
                                                    <FontAwesomeIcon style={{ marginRight: '6px' }} key={t} icon={{
                                                        'sea': faShip,
                                                        'land': faTruck,
                                                        'air': faPlane
                                                    }[t]} />
                                                ))
                                            }
                                        </TableCell>
                                        <TableCell>
                                            {form.createdBy || ''}
                                        </TableCell>
                                        <TableCell>
                                            {form.createdOn ? dayjs(form.createdOn).format('YYYY-MM-DD') : ''}
                                        </TableCell>
                                        <TableCell>
                                            <Button onClick={() => onEdit(id)}><FontAwesomeIcon icon={faPenToSquare} /></Button>
                                            <Button onClick={() => onClone(id)} sx={{ ml: 1 }}><FontAwesomeIcon icon={faClone} /></Button>
                                            <Button onClick={() => onDelete(id)} sx={{ ml: 1, color: red[500] }}><FontAwesomeIcon icon={faTrashCan} /></Button>
                                        </TableCell>
                                    </TableRow>
                                )).value()}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            )}
            {type === 'rate' && (
                <Box sx={{ padding: '0px 24px' }} >
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ width: '20%' }}></TableCell>
                                    <TableCell sx={{ width: '10%' }}></TableCell>
                                    <TableCell>Created By</TableCell>
                                    <TableCell>Created On</TableCell>
                                    <TableCell>
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {_(org.forms).pickBy(f => ((f.type === 'supplier' || f.type === 'rate') && (!f.transportationMethods || f.transportationMethods.length === 0 || _.intersection(filtertransportationModes, f.transportationMethods).length > 0) && !f.deleted)).toPairs().sortBy(f => f[1].title).map(([id, form]) => (
                                    <TableRow key={id}>
                                        <TableCell>
                                            <FontAwesomeIcon icon={faTableList} style={{ color: '#605de4' }}/>
                                            &nbsp;
                                            &nbsp;
                                            {form.title}
                                            {form.deleted ? ' - deleted' : ''}
                                            {form.type === 'supplier' ? ' (old style)' : ''}
                                        </TableCell>
                                        <TableCell sx={{ textAlign: 'center' }}>
                                            {
                                                (form.transportationMethods || (form.transportationMethod ? [form.transportationMethod] : TRANSPORT_MODES)).map(t => (
                                                    <FontAwesomeIcon style={{ marginRight: '6px' }} key={t} icon={{
                                                        'sea': faShip,
                                                        'land': faTruck,
                                                        'air': faPlane
                                                    }[t]} />
                                                ))
                                            }
                                        </TableCell>
                                        <TableCell>
                                            {form.createdBy || ''}
                                        </TableCell>
                                        <TableCell>
                                            {form.createdOn ? dayjs(form.createdOn).format('YYYY-MM-DD') : ''}
                                        </TableCell>
                                        <TableCell>
                                            <Button onClick={() => onEdit(id)}><FontAwesomeIcon icon={faPenToSquare} /></Button>
                                            <Button onClick={() => onClone(id)} sx={{ ml: 1 }}><FontAwesomeIcon icon={faClone} /></Button>
                                            <Button onClick={() => onDelete(id)} sx={{ ml: 1, color: red[500] }}><FontAwesomeIcon icon={faTrashCan} /></Button>
                                        </TableCell>
                                    </TableRow>
                                )).value()}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            )}
            <Dialog
                open={!!openDelete}
                onClose={() => setOpenDelete(null)}
            >   
                <DialogTitle>
                    Delete Rate
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete form "{(openDelete || {}).title}"?
                        {deleting === 'error' && (
                            <div>
                                <Typography variant='caption' color={red[500]}>
                                    Error deleting form. Please try again later.
                                </Typography>
                            </div>
                        )}
                    <div style={{ display: 'block', textAlign: 'center', padding: '12px' }}>
                        {deleting === 'deleting' && (
                            <CircularProgress size={18} />
                        )}
                    </div>
                    </DialogContentText>
                </DialogContent>
                {deleting !== 'deleting' && (
                    <DialogActions>
                        <Button onClick={() => setOpenDelete(null)}>No</Button>
                        <Button onClick={() => onDeleteConfirm()}>Yes</Button>
                    </DialogActions>
                )}
            </Dialog>
            <Modal open={!!editLinksOpen} onClose={() => setEditLinksOpen(null)} >
                <Paper sx={{ ...modalStyle, width: '720px'}}>
                    <FormsLink org={org} onOrgUpdated={onOrgUpdated} accesstoken={accesstoken} />
                </Paper>
            </Modal>
        </Box>
    );
}

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
};
